// Throttled, panned one-shot sounds for combat. Many hits / explosions can land in the same
// frame; one sound per id per short window keeps the mix clean. Audio failures never break combat.

import type { GameContext, LoopHandle } from '../../core/contracts';
import type { LoopId, SfxId } from '../../core/types';

export interface CombatSfx {
  /** Play `id`, skipping it if the same id played less than `gapMs` ago (real time). */
  play(id: SfxId, opts?: { volume?: number; pitch?: number; x?: number }, gapMs?: number): void;
  loop(id: LoopId, opts?: { volume?: number; pitch?: number }): LoopHandle | null;
}

const NOOP_LOOP: LoopHandle = { stop() {}, setVolume() {}, setPitch() {} };

export function createSfx(ctx: GameContext): CombatSfx {
  const last = new Map<SfxId, number>();

  /** Gentle stereo placement from the world x relative to the camera centre. */
  const panAt = (x: number | undefined): number => {
    if (x === undefined) return 0;
    try {
      const cam = ctx.scene.cameras.main;
      const p = (x - cam.midPoint.x) / (cam.width * 0.5);
      return Math.max(-1, Math.min(1, p)) * 0.55;
    } catch {
      return 0;
    }
  };

  return {
    play(id, opts, gapMs = 0) {
      const now = performance.now();
      if (gapMs > 0) {
        const t = last.get(id);
        if (t !== undefined && now - t < gapMs) return;
      }
      last.set(id, now);
      try {
        ctx.audio.play(id, { volume: opts?.volume, pitch: opts?.pitch, pan: panAt(opts?.x) });
      } catch (err) {
        console.warn('[combat] sfx failed', id, err);
      }
    },
    loop(id, opts) {
      try {
        return ctx.audio.loop(id, opts) ?? NOOP_LOOP;
      } catch (err) {
        console.warn('[combat] loop failed', id, err);
        return null;
      }
    },
  };
}

/** Small cosmetic pitch variation (Math.random is fine: purely audio-visual). */
export function jitterPitch(spread = 0.08): number {
  return 1 + (Math.random() * 2 - 1) * spread;
}
